import { ActionIcon, Alert, Box, Button, Group, Stack, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi2';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import useFieldConfigError from '@/hooks/useFieldConfigError.ts';
import { ConfigFieldValue } from '@/types/automationFields.ts';
import { IntegrationTextConfigField } from '@/types/integrations.ts';

type BrowserSecretBinding = {
  alias: string;
  sensitive_key: string;
  allowed_domains: string[];
};

type Props = {
  stepId: string;
  field: IntegrationTextConfigField;
  fieldValue: unknown;
  fieldValueConfig: ConfigFieldValue;
  onChange: (updatedValue: string) => void;
  disabled: boolean;
};

const emptyBinding = (): BrowserSecretBinding => ({
  alias: '',
  sensitive_key: '',
  allowed_domains: [],
});

const parseBindings = (value: unknown): { bindings: BrowserSecretBinding[]; isMalformed: boolean } => {
  if (value == null || value === '') {
    return { bindings: [], isMalformed: false };
  }
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (
      !Array.isArray(parsed) ||
      !parsed.every(
        (binding) =>
          binding &&
          typeof binding === 'object' &&
          typeof binding.alias === 'string' &&
          typeof binding.sensitive_key === 'string' &&
          Array.isArray(binding.allowed_domains) &&
          binding.allowed_domains.every((domain: unknown) => typeof domain === 'string'),
      )
    ) {
      return { bindings: [], isMalformed: true };
    }
    return {
      bindings: parsed.map((binding) => ({
        alias: binding.alias,
        sensitive_key: binding.sensitive_key,
        allowed_domains: binding.allowed_domains,
      })),
      isMalformed: false,
    };
  } catch {
    return { bindings: [], isMalformed: true };
  }
};

const serializeBindings = (bindings: BrowserSecretBinding[]) => JSON.stringify(bindings);

const aliasPattern = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const hostnamePattern =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

const validateBindings = (bindings: BrowserSecretBinding[]): string | null => {
  if (bindings.length > 10) {
    return 'A Browser step supports at most 10 secret bindings.';
  }

  const aliases = new Set<string>();
  const sensitiveKeys = new Set<string>();
  for (const binding of bindings) {
    if (!aliasPattern.test(binding.alias)) {
      return 'Aliases must be 1-64 lowercase letters, digits, hyphens, or underscores.';
    }
    if (!binding.sensitive_key.trim()) {
      return 'Each binding needs a secure input key.';
    }
    if (!binding.allowed_domains.length || binding.allowed_domains.length > 10) {
      return 'Each binding needs between 1 and 10 allowed hostnames.';
    }
    if (binding.allowed_domains.some((domain) => !hostnamePattern.test(domain))) {
      return 'Allowed hostnames must be bare hostnames, without a protocol or path.';
    }
    if (aliases.has(binding.alias)) {
      return 'Aliases must be unique.';
    }
    if (sensitiveKeys.has(binding.sensitive_key.trim())) {
      return 'Secure input keys must be unique.';
    }
    aliases.add(binding.alias);
    sensitiveKeys.add(binding.sensitive_key.trim());
  }
  return null;
};

const BrowserSecretBindingsField = ({
  stepId,
  field,
  fieldValue,
  fieldValueConfig,
  onChange,
  disabled,
}: Props) => {
  const error = useFieldConfigError(stepId, fieldValueConfig);
  const parsedBindings = useMemo(() => parseBindings(fieldValue), [fieldValue]);
  const [bindings, setBindings] = useState(parsedBindings.bindings);
  const [isMalformed, setIsMalformed] = useState(parsedBindings.isMalformed);
  const isDisabled = disabled || fieldValueConfig.is_disabled;
  const validationError = validateBindings(bindings);

  useEffect(() => {
    setBindings(parsedBindings.bindings);
    setIsMalformed(parsedBindings.isMalformed);
  }, [parsedBindings]);

  const updateBindings = (updater: (current: BrowserSecretBinding[]) => BrowserSecretBinding[]) =>
    setBindings((current) => updater(current));

  const saveBindings = () => {
    if (!validationError) {
      onChange(serializeBindings(bindings));
      setIsMalformed(false);
    }
  };

  return (
    <Box pos="relative">
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {field.display_name}
          {field.required ? ' *' : ''}
        </Text>
        {field.description && (
          <Text size="xs" c="dimmed">
            {field.description}
          </Text>
        )}
        <Alert color="blue" variant="light">
          Enter secure input key names only. Values remain in the secure input source and are never stored here.
          {' '}Hostnames match exactly: example.com does not permit www.example.com or other subdomains.
          Add each approved hostname explicitly. Embedded controls may require their iframe hostname.
        </Alert>
        {isMalformed && (
          <Alert color="yellow" variant="light">
            The saved binding contract is malformed. Replace it with a complete valid structured contract before
            saving.
          </Alert>
        )}
        {bindings.map((binding, index) => (
          <Group key={index} align="end" wrap="nowrap">
            <StyledTextInput
              label="Alias"
              value={binding.alias}
              placeholder="payment-card"
              onChange={(event) => {
                const alias = event.currentTarget.value;
                updateBindings((current) =>
                  current.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, alias } : item,
                  ),
                );
              }}
              disabled={isDisabled}
            />
            <StyledTextInput
              label="Secure input key"
              value={binding.sensitive_key}
              placeholder="checkout.card_number"
              onChange={(event) => {
                const sensitiveKey = event.currentTarget.value;
                updateBindings((current) =>
                  current.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, sensitive_key: sensitiveKey } : item,
                  ),
                );
              }}
              disabled={isDisabled}
            />
            <StyledTextInput
              label="Allowed hostnames"
              value={binding.allowed_domains.join(', ')}
              placeholder="payments.example.com"
              onChange={(event) => {
                const allowedDomains = event.currentTarget.value
                  .split(',')
                  .map((domain) => domain.trim())
                  .filter(Boolean);
                updateBindings((current) =>
                  current.map((item, itemIndex) =>
                    itemIndex === index
                      ? {
                          ...item,
                          allowed_domains: allowedDomains,
                        }
                      : item,
                  ),
                );
              }}
              disabled={isDisabled}
            />
            <ActionIcon
              aria-label={`Remove secret binding ${index + 1}`}
              color="red"
              variant="subtle"
              onClick={() => updateBindings((current) => current.filter((_, itemIndex) => itemIndex !== index))}
              disabled={isDisabled}
            >
              <HiOutlineTrash />
            </ActionIcon>
          </Group>
        ))}
        <Button
          leftSection={<HiOutlinePlus />}
          variant="light"
          size="xs"
          w="fit-content"
          onClick={() => updateBindings((current) => [...current, emptyBinding()])}
          disabled={isDisabled || bindings.length >= 10}
        >
          Add secret binding
        </Button>
        <Group>
          <Button
            size="xs"
            w="fit-content"
            onClick={saveBindings}
            disabled={isDisabled || Boolean(validationError)}
          >
            Save secret bindings
          </Button>
          {validationError && (
            <Text c="red" size="xs">
              {validationError}
            </Text>
          )}
        </Group>
        {error?.message && (
          <Text c="red" size="xs">
            {error.message}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export default BrowserSecretBindingsField;
